import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import api from "../lib/api";
import {
  accreditationAttestation, determinationBasis,
} from "../lib/accreditationAttestation";
import {
  FolderOpen, Search, RefreshCw, ChevronRight, CheckCircle2, XCircle,
  ShieldCheck, Award, Banknote, Handshake, Loader2, FileText, Eye, Download, Pencil,
  AlertTriangle,
} from "lucide-react";

const LIGHT_BG = {
  green:  "bg-emerald-500 ring-emerald-500/30",
  yellow: "bg-amber-400 ring-amber-400/30",
  red:    "bg-rose-500 ring-rose-500/30",
};

const DOC_STATE_BADGE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending:  "bg-amber-50 text-amber-700 border-amber-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  missing:  "bg-slate-50 text-slate-500 border-slate-200",
};

// FastAPI `detail` is a string on most endpoints and an object on the gate
// ones (`{message, missing}`). Reading `detail` raw put an object into
// `toast.error`, which renders as "[object Object]" and loses the message —
// on precisely the refusals that have something useful to say. Four other call
// sites in this codebase already do this; CAPMARKETS-179 AC0 brings the last
// one into line.
const errMsg = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail?.message) return detail.message;
  return e?.message || fallback;
};

/** The `missing` list off a gate 409, or [] when the error is anything else. */
export const gateMissingItems = (e) => {
  const missing = e?.response?.data?.detail?.missing;
  return Array.isArray(missing) ? missing : [];
};

const AdminInvestorFilePage = () => {
  const [data, setData] = useState({ users: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [activeUser, setActiveUser] = useState(null);   // row from the list
  const [detail, setDetail] = useState(null);           // compliance/admin/{id}
  const [fileUser, setFileUser] = useState(null);       // command-center user doc
  const [partnerStage, setPartnerStage] = useState(null); // command-center partner_stage (CAPMARKETS-118)
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(null);               // action key currently running
  const [pcpaAmount, setPcpaAmount] = useState("");     // optional participation amount (template v2)
  // CAPMARKETS-179: the gate's refusal, kept on the block instead of thrown at
  // a toast that disappears — and the override reason the error asks for, which
  // the panel previously had no field to type into.
  const [pcpaMissing, setPcpaMissing] = useState([]);
  const [pcpaOverride, setPcpaOverride] = useState("");
  // CAPMARKETS-178 AC4b — the negative determination and its required reason.
  const [rejectingAccreditation, setRejectingAccreditation] = useState(false);
  const [accreditationRejectReason, setAccreditationRejectReason] = useState("");
  // CAPMARKETS-052: admin edit of the member's contracting-party election.
  const [signingEdit, setSigningEdit] = useState(false);
  const [signingType, setSigningType] = useState("individual");
  const [signingEntityName, setSigningEntityName] = useState("");
  const [signingTitle, setSigningTitle] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/compliance/admin/list?filter=all&limit=300`);
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load investor files"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Monotonic request token: guards against out-of-order responses. If the admin
  // opens member A then quickly switches to B (or an action refreshes), a slower
  // earlier response must NOT overwrite the currently-active member's file — that
  // could otherwise approve/reject documents against the wrong person.
  const detailReqRef = useRef(0);

  const loadDetail = async (userId) => {
    const token = ++detailReqRef.current;
    setDetailLoading(true);
    try {
      const [comp, file] = await Promise.all([
        api.get(`/compliance/admin/${userId}`),
        api.get(`/command-center/admin/files/${userId}`),
      ]);
      if (token !== detailReqRef.current) return;   // superseded by a newer request
      setDetail(comp.data);
      setFileUser(file.data?.user || null);
      setPartnerStage(file.data?.partner_stage || null);
    } catch (e) {
      if (token === detailReqRef.current) toast.error(errMsg(e, "Failed to load member file"));
    } finally {
      if (token === detailReqRef.current) setDetailLoading(false);
    }
  };

  // Cached KYC document blobs for the OPEN drawer (docId -> { blob, url }):
  // View-then-Download shares one transfer, and object URLs stay valid until
  // the drawer closes (a flat revoke timer left reloaded viewer tabs dead).
  const kycBlobCacheRef = useRef(new Map());

  // CAPMARKETS-127: documents this reviewer has opened. Approving asserts a
  // human examined the evidence, so the server refuses an approval with no
  // access event by the acting admin (409). This mirrors that server-side
  // rule in the UI — the button is an affordance, the API is the control.
  const [viewedDocs, setViewedDocs] = useState(() => new Set());

  const releaseKycBlobs = () => {
    kycBlobCacheRef.current.forEach((e) => window.URL.revokeObjectURL(e.url));
    kycBlobCacheRef.current.clear();
  };

  useEffect(() => {
    const cache = kycBlobCacheRef.current;
    return () => {
      cache.forEach((e) => window.URL.revokeObjectURL(e.url));
      cache.clear();
    };
  }, []);

  const openDetail = (row) => {
    releaseKycBlobs();   // previous member's cached documents must not linger
    // The decline form and its typed reason are per-MEMBER state. Left
    // standing across a switch, an operator who opened member A's form and
    // then moved to member B would submit A's reason as a compliance decision
    // against B (Copilot on #2440). Reset with the blobs, for the same reason
    // the blobs are reset.
    setRejectingAccreditation(false);
    setAccreditationRejectReason("");
    setActiveUser(row);
    setDetail(null);
    setFileUser(null);
    setPartnerStage(null);
    setPcpaAmount("");   // per-member form state must not leak across files
    setPcpaMissing([]);
    setPcpaOverride("");
    setSigningEdit(false);
    loadDetail(row.id);   // loadDetail owns detailLoading
  };

  const closeDrawer = () => {
    detailReqRef.current++;   // invalidate any in-flight detail load
    releaseKycBlobs();
    setActiveUser(null);
    setDetail(null);
    setFileUser(null);
    setPartnerStage(null);
    setDetailLoading(false);
  };

  // --- Actions wired to existing endpoints (no backend changes) ---

  const refreshAfterAction = async () => {
    if (activeUser?.id) await loadDetail(activeUser.id);
    await load();
  };

  const reviewDoc = async (docId, status) => {
    const key = `doc-${docId}-${status}`;
    setBusy(key);
    try {
      await api.put(`/kyc/${docId}/review`, null, { params: { status } });
      toast.success(`Document ${status}`);
      await refreshAfterAction();
    } catch (e) {
      toast.error(errMsg(e, "Could not update document"));
    } finally {
      setBusy(null);
    }
  };

  // GET /admin/kyc/documents/{doc_id} is the ONLY admin path that returns KYC
  // file bytes — every listing endpoint strips file_data. Blob URLs keep the
  // bearer-token flow (a plain <a href> would 401).
  const getKycDocEntry = async (docId) => {
    const cached = kycBlobCacheRef.current.get(docId);
    // A cache hit still means this reviewer has opened the bytes — the server
    // recorded the access event on the original fetch (CAPMARKETS-127).
    if (cached) return cached;
    const blob = (await api.get(`/admin/kyc/documents/${docId}`, { responseType: "blob" })).data;
    const entry = { blob, url: window.URL.createObjectURL(blob) };
    kycBlobCacheRef.current.set(docId, entry);
    setViewedDocs((prev) => new Set(prev).add(docId));
    return entry;
  };

  const triggerBlobDownload = (url, fileName) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "document";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Types the tab can actually render; anything else (heic/tiff/unknown)
  // falls back to a download instead of a broken viewer tab.
  const RENDERABLE_TYPES = new Set([
    "application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp",
  ]);

  const viewKycDoc = async (docId, fileName) => {
    // Open the window synchronously, inside the click gesture — a window.open
    // issued after an await is popup-blocked (Safari always, Chrome on slow
    // networks). NB: passing "noopener" in features makes window.open return
    // null even on success, so the opener is nulled by hand instead.
    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Popup blocked — allow popups to view documents");
      return;
    }
    win.opener = null;
    setBusy(`doc-view-${docId}`);
    try {
      const entry = await getKycDocEntry(docId);
      if (!RENDERABLE_TYPES.has(entry.blob.type)) {
        win.close();
        triggerBlobDownload(entry.url, fileName);
        toast.info("Preview isn't supported for this file type — downloaded instead");
        return;
      }
      win.location = entry.url;
    } catch {
      win.close();
      toast.error("Could not open the document");
    } finally {
      setBusy(null);
    }
  };

  const downloadKycDoc = async (docId, fileName) => {
    setBusy(`doc-download-${docId}`);
    try {
      const entry = await getKycDocEntry(docId);
      triggerBlobDownload(entry.url, fileName);
    } catch {
      toast.error("Could not download the document");
    } finally {
      setBusy(null);
    }
  };

  const recomputeKycStatus = async () => {
    setBusy("kyc-recompute");
    try {
      const r = await api.post(`/kyc/admin/recompute-status`, null, {
        params: { user_id: activeUser.id },
      });
      if (r.data.flipped?.length) {
        toast.success("Member KYC status recomputed — now approved");
      } else {
        const reason = r.data.skipped?.[0]?.reason || "no change";
        toast.info(`No change: ${reason}`);
      }
      await refreshAfterAction();
    } catch (e) {
      toast.error(errMsg(e, "Could not recompute KYC status"));
    } finally {
      setBusy(null);
    }
  };

  const markAccredited = async () => {
    setBusy("accredited");
    try {
      await api.post(`/onboarding/admin/mark-accredited-approved`, { user_id: activeUser.id });
      toast.success("Marked accredited / approved");
      await refreshAfterAction();
    } catch (e) {
      toast.error(errMsg(e, "Could not mark accredited"));
    } finally {
      setBusy(null);
    }
  };

  // STARSEED-CAPMARKETS-178 AC4b. Before this the card offered approval and
  // nothing else, so a reviewer who concluded the member does not qualify had
  // no control to press and the file sat at `in_review` indefinitely — and
  // `accreditation_state`'s `rejected` branch, which this same card already
  // renders, was unreachable from any UI. A review surface that can only end
  // one way teaches the operator to approve.
  //
  // A reason is required. The decision is written to the member's audit trail,
  // and "rejected, no reason given" is not a record anyone can act on later.
  const rejectAccreditation = async () => {
    const note = accreditationRejectReason.trim();
    if (!note) return;
    setBusy("accredited-reject");
    try {
      await api.post(`/verification/admin/accreditation/decision/reject`, {
        user_id: activeUser.id, note,
      });
      toast.success("Accreditation declined");
      setAccreditationRejectReason("");
      setRejectingAccreditation(false);
      await refreshAfterAction();
    } catch (e) {
      toast.error(errMsg(e, "Could not decline accreditation"));
    } finally {
      setBusy(null);
    }
  };

  const markQualified = async () => {
    setBusy("qualified");
    try {
      await api.post(`/qualification/mark-qualified/${activeUser.id}`);
      toast.success("Marked qualified");
      await refreshAfterAction();
    } catch (e) {
      toast.error(errMsg(e, "Could not mark qualified"));
    } finally {
      setBusy(null);
    }
  };

  const markPartnerComplete = async () => {
    setBusy("partner");
    try {
      await api.post(`/onboarding/admin/mark-partner-onboarding-complete`, { user_id: activeUser.id });
      toast.success("Partner onboarding marked complete");
      await refreshAfterAction();
    } catch (e) {
      toast.error(errMsg(e, "Could not mark partner onboarding complete"));
    } finally {
      setBusy(null);
    }
  };

  const markFundTransferComplete = async () => {
    setBusy("fund-transfer");
    try {
      await api.post(`/onboarding/admin/mark-fund-transfer-complete`, { user_id: activeUser.id });
      toast.success("Fund transfer marked complete");
      await refreshAfterAction();
    } catch (e) {
      toast.error(errMsg(e, "Could not mark fund transfer complete"));
    } finally {
      setBusy(null);
    }
  };

  // `override` is deliberately an explicit argument rather than "send whatever
  // is in the reason box": the ordinary Send button must not silently become an
  // override because the operator typed something and changed their mind
  // (CAPMARKETS-179 AC4).
  const sendParticipationAgreement = async (resend = false, { override = false } = {}) => {
    const reason = pcpaOverride.trim();
    // Mirrors the server's whitespace check. The server is the control; this
    // stops the panel offering a click that cannot succeed (AC5).
    if (override && !reason) return;

    setBusy(override ? "pcpa-override" : "pcpa");
    try {
      const body = { user_id: activeUser.id, resend };
      // Optional (template v2, forge-20260611-008): when blank, the
      // agreement's ruled amount line stays open for manual fill.
      const amt = parseFloat(pcpaAmount);
      if (!Number.isNaN(amt) && amt > 0) body.participation_amount = amt;
      if (override) body.note = reason;
      const r = await api.post(`/onboarding/admin/send-participation-agreement`, body);
      toast.success(`Participation agreement ${resend ? "re-" : ""}sent to partner (No. ${r.data?.pcpa_agreement_number})`);
      setPcpaMissing([]);
      setPcpaOverride("");
      await refreshAfterAction();
    } catch (e) {
      // A gate refusal names actionable items; keep them on the block rather
      // than in a toast the operator cannot re-read. Only a gate error may
      // REPLACE the list: a 502/504 from the email provider says nothing about
      // whether the member completed anything, and clearing on it would take
      // the override affordance away at the exact moment the server is telling
      // the operator that retrying is safe.
      const gateItems = gateMissingItems(e);
      if (gateItems.length) setPcpaMissing(gateItems);
      toast.error(errMsg(e, "Could not send participation agreement"));
    } finally {
      setBusy(null);
    }
  };

  const startSigningEdit = () => {
    const se = fileUser?.signing_entity;
    setSigningType(se?.type === "entity" ? "entity" : "individual");
    setSigningEntityName(se?.entity_name || "");
    setSigningTitle(se?.signer_title || "");
    setSigningEdit(true);
  };

  const saveSigningEntity = async () => {
    if (signingType === "entity" && !signingEntityName.trim()) {
      toast.error("Enter the entity's exact legal name");
      return;
    }
    setBusy("signing-entity");
    try {
      const r = await api.put(`/admin/users/${activeUser.id}/signing-entity`, {
        type: signingType,
        entity_name: signingType === "entity" ? signingEntityName : null,
        signer_title: signingType === "entity" ? (signingTitle || null) : null,
      });
      setSigningEdit(false);
      if (r.data?.active_signed_agreement) {
        // The e-signed artifact is sealed — the new party only reaches it
        // through the Partner Stage void + re-sign flow.
        toast.warning(
          "Saved — but this member has a SIGNED agreement. Void it in Partner Stage so they can re-sign under the new name.",
          { duration: 10000 },
        );
      } else {
        toast.success("Signing party updated — resend the agreement to apply it");
      }
      await refreshAfterAction();
    } catch (e) {
      toast.error(errMsg(e, "Could not update the signing party"));
    } finally {
      setBusy(null);
    }
  };

  // --- Derived status flags for the active member ---
  // CAPMARKETS-177: the badge used to render `fileUser.accredited_status` from
  // /command-center/admin/files while the stage light beside it was computed by
  // /compliance/admin/{id}. Two endpoints, one fact, and they disagreed on
  // screen — a red "verification not started" light above an `in_review` badge.
  // Both now render the server's canonical state; `fileUser` is only a fallback
  // for the window before the detail payload lands.
  const accredited = (detail?.accreditation_state
    || fileUser?.accredited_status || "").toLowerCase();
  // Display and done-ness are two different questions and only the first
  // follows the canonical state. The button writes `users.accredited_status`,
  // so its disabled-ness keys on THAT field: a green verification record with
  // an unset user field is exactly the desync this button exists to reconcile
  // (the "George case" in test_admin_approval_consistency.py), and disabling it
  // there strands the member short of the participation-agreement gate, which
  // reads the user field via `onboarding_checklist.accredited_done`.
  const isAccredited = ["approved", "verified"]
    .includes((fileUser?.accredited_status || "").toLowerCase());
  const accreditationNeedsAttention = accredited === "rejected";
  const attestation = accreditationAttestation(detail);
  const basis = determinationBasis(detail);
  const kycApproved = (fileUser?.kyc_status || detail?.kyc_status) === "approved";
  const isQualified = (fileUser?.qualification_status || detail?.qualification_status) === "qualified";
  const transferred = !!fileUser?.transferred_to_partner_at;
  const partnerComplete = !!fileUser?.partner_onboarding_complete;
  const fundTransferComplete = (fileUser?.fund_transfer_status || "").toLowerCase() === "completed";
  const pcpaSent = !!fileUser?.pcpa_sent_at;
  // STARSEED-CAPMARKETS-118. The Partner Onboarding and Fund Transfer cards
  // come from the legacy regulated-partner ladder and read user fields the
  // agreement flow never writes, so on a member the agreement owns they said
  // "not transferred" beside a countersigned file. Which ladder owns the stage,
  // and what records funding, is decided server-side beside the status engine
  // (`partner_stage_context`); the page only reads the answer. With no
  // `partner_stage` (an API older than this change) both cards render as they
  // always did.
  const agreementOwnsPartnerStage = partnerStage?.owner === "finbpm_agreement";
  const fundingProvider = partnerStage?.funding_provider || null;
  const partnerLegacyState = partnerComplete ? "complete" : transferred ? "in progress" : "not transferred";
  // Paytiko (or an API that predates `partner_stage`) is the only case where a
  // blank field can mean "no transfer yet"; otherwise nothing but the manual
  // override writes it, so a blank is only "not recorded".
  const paytikoOrUnknown = fundingProvider === null || fundingProvider === "paytiko";
  const fundTransferLabel = fundTransferComplete
    ? "completed"
    : (fileUser?.fund_transfer_status || (paytikoOrUnknown ? "not started" : "not recorded"));

  const filtered = data.users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.full_name || "").toLowerCase().includes(q) ||
           (u.email || "").toLowerCase().includes(q) ||
           (u.internal_email || "").toLowerCase().includes(q);
  });

  return (
    <DashboardLayout
      title="Investor File"
      subtitle="Open a member's file and approve each stage with one click"
    >
      {/* Search + refresh */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-8 bg-white"
            data-testid="investor-search"
          />
        </div>
        <Button onClick={load} variant="outline" size="sm" className="gap-1.5" data-testid="investor-refresh">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* List */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm" data-testid="investor-empty">
              No members found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="investor-table">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Member</th>
                    <th className="px-4 py-3 font-semibold">KYC</th>
                    <th className="px-4 py-3 font-semibold">Qualification</th>
                    <th className="px-4 py-3 font-semibold">Next action</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50 transition-colors" data-testid={`investor-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-slate-900">{u.full_name}</p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                        <Badge variant="outline" className="mt-1 text-[10px]">{u.role}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={u.kyc_status === "approved"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-50 text-slate-600 border-slate-200"}>
                          {u.kyc_status || "pending"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={u.qualification_status === "qualified"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-50 text-slate-600 border-slate-200"}>
                          {u.qualification_status || "pending"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate">
                        {u.next_action_label || "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button onClick={() => openDetail(u)} variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-testid={`investor-open-${u.id}`}>
                          Open file <ChevronRight size={12} className="ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail drawer */}
      {activeUser && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex justify-end" onClick={closeDrawer} data-testid="investor-drawer">
          <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <FolderOpen className="text-blue-600 shrink-0 mt-1" size={20} />
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Investor File</p>
                  <h2 className="text-xl font-serif text-slate-900">{activeUser.full_name}</h2>
                  <p className="text-sm text-slate-500">{activeUser.email}</p>
                </div>
              </div>
              <Button onClick={closeDrawer} variant="ghost" size="sm">Close ✕</Button>
            </div>

            <div className="p-6 space-y-6">
              {detailLoading || !detail ? (
                <div className="h-40 bg-slate-100 animate-pulse rounded" data-testid="investor-detail-loading" />
              ) : (
                <>
                  {/* Stage lights summary */}
                  <div className="space-y-2">
                    {detail.stages.map((s, i) => (
                      <div key={s.key} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
                        <span className={`inline-flex h-3 w-3 rounded-full ${LIGHT_BG[s.light]} ring-4 shrink-0`} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">0{i + 1}. {s.label}</p>
                          <p className="text-xs text-slate-500">{s.summary}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 1) KYC documents + per-doc actions */}
                  <section data-testid="section-kyc">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldCheck size={15} className="text-slate-500" />
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">KYC Documents</p>
                    </div>

                    {/* STARSEED-CAPMARKETS-247 — one slot holds more than one
                        document while a required slot is empty.

                        The list below renders `kyc_docs`, which the server
                        builds from `latest_by_type` — newest per slot, older
                        ones dropped. So when a member's utility bill lands as a
                        second Government ID, this section shows their utility
                        bill under Government ID and their driver's license
                        nowhere at all. Without this line the reviewer has no
                        way to know a superseded document exists.

                        Phrased as a prompt to look, not a verdict: two uploads
                        to one slot is ordinary on its own. */}
                    {detail.kyc_misfiled_slot && (
                      <div
                        className="mb-2 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900"
                        data-testid="kyc-misfiled-slot-warning"
                      >
                        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
                        <p>
                          <span className="font-semibold">{detail.kyc_misfiled_slot.slot_label}</span>
                          {" "}has {detail.kyc_misfiled_slot.count} uploads while{" "}
                          <span className="font-semibold">
                            {detail.kyc_misfiled_slot.empty_required.map((e) => e.label).join(" and ")}
                          </span>
                          {" "}{detail.kyc_misfiled_slot.empty_required.length > 1 ? "are" : "is"} empty.
                          Only the newest is shown below — open it and check whether it belongs in the
                          empty slot.
                        </p>
                      </div>
                    )}

                    <ul className="space-y-2">
                      {detail.kyc_docs.map((d) => (
                        <li key={d.type} className="flex items-center justify-between gap-3 p-2.5 rounded border border-slate-100 text-sm">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900">
                              {d.label}
                              {d.required && <span className="ml-2 text-[10px] uppercase text-rose-600 font-semibold">required</span>}
                            </p>
                            {d.file_name && <p className="text-[11px] text-slate-500 truncate">{d.file_name}</p>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className={DOC_STATE_BADGE[d.state] || DOC_STATE_BADGE.missing}>{d.state}</Badge>
                            {d.doc_id && (
                              <>
                                <Button
                                  size="sm" variant="outline"
                                  className="h-7 px-2 gap-1 border-slate-200 text-slate-700 hover:bg-slate-50"
                                  disabled={busy === `doc-view-${d.doc_id}`}
                                  onClick={() => viewKycDoc(d.doc_id, d.file_name)}
                                  title="Open the uploaded file in a new tab"
                                  data-testid={`doc-view-${d.type}`}
                                >
                                  {busy === `doc-view-${d.doc_id}`
                                    ? <Loader2 size={12} className="animate-spin" />
                                    : <Eye size={12} />} View
                                </Button>
                                <Button
                                  size="sm" variant="outline"
                                  className="h-7 w-7 p-0 border-slate-200 text-slate-700 hover:bg-slate-50"
                                  disabled={busy === `doc-download-${d.doc_id}`}
                                  onClick={() => downloadKycDoc(d.doc_id, d.file_name)}
                                  title="Download the uploaded file"
                                  data-testid={`doc-download-${d.type}`}
                                >
                                  {busy === `doc-download-${d.doc_id}`
                                    ? <Loader2 size={12} className="animate-spin" />
                                    : <Download size={12} />}
                                </Button>
                                {/* CAPMARKETS-127 — approving asserts a human
                                    examined the evidence, so it stays disabled
                                    until this reviewer opens the document. The
                                    server enforces the same rule (409); after a
                                    page reload the reviewer clicks View again,
                                    which fails in the safe direction. */}
                                <Button
                                  size="sm" variant="outline"
                                  className="h-7 px-2 gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                  disabled={
                                    d.state === "approved"
                                    || busy === `doc-${d.doc_id}-approved`
                                    || !viewedDocs.has(d.doc_id)
                                  }
                                  title={
                                    viewedDocs.has(d.doc_id)
                                      ? "Approve this document"
                                      : "Open the document with View before approving it"
                                  }
                                  onClick={() => reviewDoc(d.doc_id, "approved")}
                                  data-testid={`doc-approve-${d.type}`}
                                >
                                  {busy === `doc-${d.doc_id}-approved`
                                    ? <Loader2 size={12} className="animate-spin" />
                                    : <CheckCircle2 size={12} />} Approve
                                </Button>
                                <Button
                                  size="sm" variant="outline"
                                  className="h-7 px-2 gap-1 border-rose-200 text-rose-700 hover:bg-rose-50"
                                  disabled={d.state === "rejected" || busy === `doc-${d.doc_id}-rejected`}
                                  onClick={() => reviewDoc(d.doc_id, "rejected")}
                                  data-testid={`doc-reject-${d.type}`}
                                >
                                  {busy === `doc-${d.doc_id}-rejected`
                                    ? <Loader2 size={12} className="animate-spin" />
                                    : <XCircle size={12} />} Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                    {!kycApproved && (
                      <div className="mt-2 flex items-center justify-between gap-3 p-2.5 rounded border border-amber-200 bg-amber-50">
                        <p className="text-[11px] text-amber-800">
                          If every document above is approved but the member-level
                          KYC status hasn't flipped, recompute it from the documents
                          (legacy uploads predate the automatic flip —
                          forge-20260611-004). No effect while documents are still
                          pending or rejected.
                        </p>
                        <Button
                          size="sm" variant="outline"
                          className="h-7 px-2 shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100"
                          disabled={busy === "kyc-recompute"}
                          onClick={recomputeKycStatus}
                          data-testid="kyc-recompute-btn"
                        >
                          {busy === "kyc-recompute" ? <Loader2 size={12} className="animate-spin" /> : "Recompute from documents"}
                        </Button>
                      </div>
                    )}
                  </section>

                  {/* 2) Accreditation */}
                  <ActionBlock
                    icon={Award}
                    title="Accreditation"
                    statusBadge={
                      <Badge className={accredited === "approved"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : accreditationNeedsAttention
                          ? "bg-rose-50 text-rose-700 border-rose-200"
                          : "bg-slate-50 text-slate-600 border-slate-200"}>
                        {accredited || "not_started"}
                      </Badge>
                    }
                  >
                    <div
                      className={`mb-3 p-2.5 rounded border ${attestation.isMemberEvidence
                        ? "border-slate-200 bg-slate-50"
                        : "border-amber-200 bg-amber-50"}`}
                      data-testid={`accreditation-attestation-${attestation.kind}`}
                    >
                      <p className={`text-[11px] ${attestation.isMemberEvidence
                        ? "text-slate-700" : "text-amber-800"}`}>
                        {attestation.headline}
                      </p>

                      {/* CAPMARKETS-178 AC9 — the three questions that DECIDE
                          accreditation, as their own group beside the evidence
                          submitted for them. They were always first in the
                          grid below, but "first among ten rows" is not the
                          same as "at the decision point". This is the most the
                          surface can honestly say while OQ-178-2 (which Reg D
                          exemption, which jurisdiction) is open: it shows the
                          claim and the evidence, and leaves sufficiency to the
                          reviewer rather than encoding a standard nobody has
                          chosen. */}
                      {attestation.eligibilityClaims.length > 0 && (
                        <dl
                          className="mt-2 grid grid-cols-3 gap-2"
                          data-testid="accreditation-eligibility-claims"
                        >
                          {attestation.eligibilityClaims.map((c) => (
                            <div key={c.label}
                                 className="p-1.5 rounded border border-slate-200 bg-white">
                              <dt className="text-[10px] uppercase tracking-wide text-slate-500">{c.label}</dt>
                              <dd className="text-[11px] font-semibold text-slate-900">{c.value}</dd>
                            </div>
                          ))}
                        </dl>
                      )}

                      {attestation.answers.length > 0 && (
                        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                          {attestation.answers.map((a) => (
                            <div key={a.label} className="flex justify-between gap-2">
                              <dt className="text-[11px] text-slate-500">{a.label}</dt>
                              <dd className="text-[11px] font-medium text-slate-900 text-right">{a.value}</dd>
                            </div>
                          ))}
                        </dl>
                      )}

                      {attestation.acknowledgments.length > 0 && (
                        <ul className="mt-2 space-y-0.5">
                          {attestation.acknowledgments.map((ack) => (
                            <li key={ack.label} className="flex items-start gap-1.5 text-[11px] text-slate-600">
                              {ack.accepted
                                ? <CheckCircle2 size={11} className="mt-0.5 shrink-0 text-emerald-600" />
                                : <XCircle size={11} className="mt-0.5 shrink-0 text-rose-500" />}
                              <span>{ack.label}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {attestation.trail.length > 0 && (
                        <ul className="mt-2 pt-2 border-t border-slate-200/70 space-y-0.5">
                          {attestation.trail.map((entry, i) => (
                            <li key={`${entry.at || i}-${entry.status || ""}`}
                                className="text-[10px] text-slate-500">
                              {(entry.at || "").slice(0, 10)} · {entry.status || "—"} · {entry.by || "—"}
                              {entry.note ? ` · ${entry.note}` : ""}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* CAPMARKETS-178 AC4 — per-item review, the same shape
                        the KYC section above already has. The controls call
                        the SAME endpoints (`/kyc/{id}/review`,
                        `/admin/kyc/documents/{id}`), so the CAPMARKETS-127
                        view-gate, the -135 four-eyes rule and the access log
                        are inherited rather than re-implemented (AC5, AC8). */}
                    <div className="mb-3" data-testid="accreditation-evidence">
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                        Evidence
                      </p>
                      <ul className="space-y-2">
                        {(detail.accreditation_items || []).map((item) => (
                          <li
                            key={item.type}
                            className="flex items-center justify-between gap-3 p-2.5 rounded border border-slate-100 text-sm"
                            data-testid={`accreditation-item-${item.type}`}
                          >
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900">{item.label}</p>
                              <p className="text-[11px] text-slate-500 truncate">
                                {item.file_name || item.hint}
                              </p>
                              {/* AC10 — who decided, and when. Shown rather
                                  than merely stored: a timestamp nobody can
                                  read is not a record. */}
                              {item.reviewed_at && (
                                <p className="text-[10px] text-slate-400">
                                  Decided {String(item.reviewed_at).slice(0, 10)}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Badge className={DOC_STATE_BADGE[item.state] || DOC_STATE_BADGE.missing}>
                                {item.state}
                              </Badge>
                              {item.doc_id && (
                                <>
                                  <Button
                                    size="sm" variant="outline"
                                    className="h-7 px-2 gap-1 border-slate-200 text-slate-700 hover:bg-slate-50"
                                    disabled={busy === `doc-view-${item.doc_id}`}
                                    onClick={() => viewKycDoc(item.doc_id, item.file_name)}
                                    title="Open the uploaded file in a new tab"
                                    data-testid={`accreditation-view-${item.type}`}
                                  >
                                    {busy === `doc-view-${item.doc_id}`
                                      ? <Loader2 size={12} className="animate-spin" />
                                      : <Eye size={12} />} View
                                  </Button>
                                  <Button
                                    size="sm" variant="outline"
                                    className="h-7 w-7 p-0 border-slate-200 text-slate-700 hover:bg-slate-50"
                                    disabled={busy === `doc-download-${item.doc_id}`}
                                    onClick={() => downloadKycDoc(item.doc_id, item.file_name)}
                                    title="Download the uploaded file"
                                    data-testid={`accreditation-download-${item.type}`}
                                  >
                                    {busy === `doc-download-${item.doc_id}`
                                      ? <Loader2 size={12} className="animate-spin" />
                                      : <Download size={12} />}
                                  </Button>
                                  {/* Disabled until this reviewer has opened
                                      the bytes. The server enforces the same
                                      rule with a 409 — this is the affordance,
                                      that is the control. */}
                                  <Button
                                    size="sm" variant="outline"
                                    className="h-7 px-2 gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                    disabled={
                                      item.state === "approved"
                                      || busy === `doc-${item.doc_id}-approved`
                                      || !viewedDocs.has(item.doc_id)
                                    }
                                    title={
                                      viewedDocs.has(item.doc_id)
                                        ? "Approve this item"
                                        : "Open the document with View before approving it"
                                    }
                                    onClick={() => reviewDoc(item.doc_id, "approved")}
                                    data-testid={`accreditation-approve-${item.type}`}
                                  >
                                    {busy === `doc-${item.doc_id}-approved`
                                      ? <Loader2 size={12} className="animate-spin" />
                                      : <CheckCircle2 size={12} />} Approve
                                  </Button>
                                  <Button
                                    size="sm" variant="outline"
                                    className="h-7 px-2 gap-1 border-rose-200 text-rose-700 hover:bg-rose-50"
                                    disabled={item.state === "rejected" || busy === `doc-${item.doc_id}-rejected`}
                                    onClick={() => reviewDoc(item.doc_id, "rejected")}
                                    data-testid={`accreditation-reject-${item.type}`}
                                  >
                                    {busy === `doc-${item.doc_id}-rejected`
                                      ? <Loader2 size={12} className="animate-spin" />
                                      : <XCircle size={12} />} Reject
                                  </Button>
                                </>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* AC6 — what the last approval rested on. An approval over
                        nothing says so here, rather than looking identical to
                        a reviewed one. */}
                    {basis && (
                      <div
                        className={`mb-3 p-2 rounded border text-[11px] ${basis.isEvidenced
                          ? "border-slate-200 bg-slate-50 text-slate-700"
                          : "border-amber-200 bg-amber-50 text-amber-800"}`}
                        data-testid={`accreditation-basis-${basis.kind}`}
                      >
                        {basis.text}
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        className="gap-1.5"
                        disabled={isAccredited || busy === "accredited"}
                        onClick={markAccredited}
                        data-testid="action-mark-accredited"
                      >
                        {busy === "accredited" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                        {isAccredited ? "Accredited approved" : "Mark accredited approved"}
                      </Button>
                      {!rejectingAccreditation && (
                        <Button
                          size="sm" variant="outline"
                          className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50"
                          disabled={accreditationNeedsAttention || busy === "accredited-reject"}
                          onClick={() => setRejectingAccreditation(true)}
                          data-testid="action-reject-accreditation"
                        >
                          <XCircle size={14} />
                          {accreditationNeedsAttention ? "Accreditation declined" : "Decline accreditation"}
                        </Button>
                      )}
                    </div>

                    {rejectingAccreditation && (
                      <div className="mt-2 p-2.5 rounded border border-rose-200 bg-rose-50 space-y-2"
                           data-testid="accreditation-reject-form">
                        <Label className="text-[11px] text-rose-900">
                          Why is this member not accredited? Recorded on their audit trail.
                        </Label>
                        <Input
                          value={accreditationRejectReason}
                          onChange={(e) => setAccreditationRejectReason(e.target.value)}
                          placeholder="e.g. Verification letter is older than the review window"
                          className="h-8 text-sm bg-white"
                          data-testid="accreditation-reject-reason"
                        />
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm" variant="outline"
                            className="h-7 px-2 border-rose-300 text-rose-800 hover:bg-rose-100"
                            disabled={!accreditationRejectReason.trim() || busy === "accredited-reject"}
                            onClick={rejectAccreditation}
                            data-testid="accreditation-reject-confirm"
                          >
                            {busy === "accredited-reject"
                              ? <Loader2 size={12} className="animate-spin" />
                              : "Record declination"}
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 px-2 text-slate-600"
                            onClick={() => {
                              setRejectingAccreditation(false);
                              setAccreditationRejectReason("");
                            }}
                            data-testid="accreditation-reject-cancel"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </ActionBlock>

                  {/* 3) Qualification */}
                  <ActionBlock
                    icon={Banknote}
                    title="Qualification"
                    statusBadge={
                      <Badge className={isQualified
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-50 text-slate-600 border-slate-200"}>
                        {fileUser?.qualification_status || detail?.qualification_status || "pending"}
                      </Badge>
                    }
                    hint={!kycApproved && !isQualified ? "KYC must be approved before marking qualified." : null}
                  >
                    <Button
                      size="sm"
                      className="gap-1.5"
                      disabled={isQualified || !kycApproved || busy === "qualified"}
                      onClick={markQualified}
                      data-testid="action-mark-qualified"
                    >
                      {busy === "qualified" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      {isQualified ? "Qualified" : "Mark qualified"}
                    </Button>
                  </ActionBlock>

                  {/* 4) Participation Agreement — generated from the partner's
                       template and emailed to the partner-enablement contact
                       so the member can be enabled in the partner API
                       (forge-20260609-002). Precedes partner onboarding. */}
                  <ActionBlock
                    icon={FileText}
                    title="Participation Agreement"
                    statusBadge={
                      <Badge className={pcpaSent
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-50 text-slate-600 border-slate-200"}>
                        {pcpaSent ? `sent · ${fileUser?.pcpa_agreement_number || ""}` : "not sent"}
                      </Badge>
                    }
                    hint={!pcpaSent
                      ? "Send when the member opens a membership — the partner needs it to enable them."
                      : null}
                  >
                    <div className="mb-2 p-2.5 rounded border border-slate-200 bg-slate-50" data-testid="signing-party-block">
                      {!signingEdit ? (
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs text-slate-700">
                            Signing as:{" "}
                            {/* Server-computed (SumSub-first signatory) so the
                                console shows exactly what the PDF renders. */}
                            <span className="font-semibold" data-testid="signing-party-display">
                              {fileUser?.signing_party_display
                                || `${fileUser?.full_name || "member"} (individual)`}
                            </span>
                          </p>
                          <Button
                            size="sm" variant="outline"
                            className="h-7 px-2 gap-1 border-slate-200 text-slate-700 hover:bg-white"
                            onClick={startSigningEdit}
                            data-testid="signing-party-edit"
                          >
                            <Pencil size={12} /> Edit
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={signingType === "individual" ? "default" : "outline"}
                              className="h-7 px-2"
                              onClick={() => setSigningType("individual")}
                              data-testid="signing-party-individual"
                            >
                              Individual
                            </Button>
                            <Button
                              size="sm"
                              variant={signingType === "entity" ? "default" : "outline"}
                              className="h-7 px-2"
                              onClick={() => setSigningType("entity")}
                              data-testid="signing-party-entity"
                            >
                              Entity
                            </Button>
                          </div>
                          {signingType === "entity" && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <Input
                                value={signingEntityName}
                                onChange={(e) => setSigningEntityName(e.target.value)}
                                placeholder="Entity legal name, e.g. JC Capital Funding"
                                className="h-8 bg-white text-slate-900 border-slate-300"
                                data-testid="signing-party-name-input"
                              />
                              <Input
                                value={signingTitle}
                                onChange={(e) => setSigningTitle(e.target.value)}
                                placeholder="Signer title (optional), e.g. Managing Member"
                                className="h-8 bg-white text-slate-900 border-slate-300"
                                data-testid="signing-party-title-input"
                              />
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="h-7 px-2"
                              disabled={busy === "signing-entity" || (signingType === "entity" && !signingEntityName.trim())}
                              onClick={saveSigningEntity}
                              data-testid="signing-party-save"
                            >
                              {busy === "signing-entity" ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Save
                            </Button>
                            <Button
                              size="sm" variant="outline" className="h-7 px-2"
                              onClick={() => setSigningEdit(false)}
                            >
                              Cancel
                            </Button>
                          </div>
                          {pcpaSent && (
                            <p className="text-[11px] text-amber-700">
                              Already sent as No. {fileUser?.pcpa_agreement_number || "—"} — after saving,
                              use "Resend agreement to partner" to reissue it under the new name (same number).
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="mb-2">
                      <Label className="text-xs text-slate-600">Participation amount (USD, optional)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="1000"
                        value={pcpaAmount}
                        onChange={(e) => setPcpaAmount(e.target.value)}
                        placeholder="leave blank to keep the ruled line open"
                        className="mt-1 h-8 max-w-xs bg-white text-slate-900 border-slate-300"
                        data-testid="pcpa-amount-input"
                      />
                    </div>
                    <Button
                      size="sm"
                      className="gap-1.5"
                      disabled={busy === "pcpa" || busy === "pcpa-override"}
                      onClick={() => sendParticipationAgreement(pcpaSent)}
                      data-testid="action-send-participation-agreement"
                    >
                      {busy === "pcpa" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      {pcpaSent ? "Resend agreement to partner" : "Send agreement to partner"}
                    </Button>

                    {/* CAPMARKETS-179: the gate refused, and the refusal names
                        actionable items. The old panel dropped that sentence
                        into a toast and offered no way to do either of the two
                        things it asked for. */}
                    {pcpaMissing.length > 0 && (
                      <div
                        className="mt-3 p-2.5 rounded border border-amber-200 bg-amber-50"
                        data-testid="pcpa-gate-blocked"
                      >
                        <p className="text-[11px] font-semibold text-amber-900">
                          Blocked — the member has not completed:
                        </p>
                        <ul className="mt-1 space-y-0.5">
                          {pcpaMissing.map((m) => (
                            <li key={m.key} className="text-[11px] text-amber-800">
                              {m.label}
                              {m.portal_path && (
                                <span className="text-amber-700"> — member completes this at <code>{m.portal_path}</code></span>
                              )}
                            </li>
                          ))}
                        </ul>
                        <p className="mt-2 text-[11px] text-amber-800">
                          These are member actions — there is no admin path to any
                          of them, by design. The quicker route is usually to ask
                          the member to finish them.
                        </p>

                        <div className="mt-2 pt-2 border-t border-amber-200">
                          <Label className="text-xs text-amber-900">Override reason (required to send anyway)</Label>
                          <Input
                            value={pcpaOverride}
                            onChange={(e) => setPcpaOverride(e.target.value)}
                            placeholder="e.g. partner needs the agreement before month-end; consent chased separately 2026-08-24"
                            className="mt-1 h-8 bg-white text-slate-900 border-amber-300"
                            data-testid="pcpa-override-input"
                          />
                          <p className="mt-1 text-[11px] text-amber-800" data-testid="pcpa-override-disclosure">
                            Overriding sends this member's data to the partner with
                            the items above still outstanding. The reason is recorded
                            against the member and against you.
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2 gap-1.5 border-amber-300 text-amber-900 hover:bg-amber-100"
                            disabled={!pcpaOverride.trim() || busy === "pcpa" || busy === "pcpa-override"}
                            onClick={() => sendParticipationAgreement(pcpaSent, { override: true })}
                            data-testid="action-send-participation-agreement-override"
                          >
                            {busy === "pcpa-override"
                              ? <Loader2 size={14} className="animate-spin" />
                              : <XCircle size={14} />}
                            Send anyway, with reason
                          </Button>
                        </div>
                      </div>
                    )}
                  </ActionBlock>

                  {/* 5) Partner onboarding — the partner-side completion signal
                       (legacy ladder). On a member the agreement owns it is
                       inapplicable, not absent: the legacy flags stay visible
                       because a partially migrated member needs them seen
                       (CAPMARKETS-118 shaping, question 1). */}
                  <ActionBlock
                    icon={Handshake}
                    title="Partner Onboarding"
                    statusBadge={agreementOwnsPartnerStage ? (
                      <Badge className="bg-slate-50 text-slate-600 border-slate-200"
                        data-testid="partner-onboarding-legacy-badge">
                        legacy · {partnerLegacyState}
                      </Badge>
                    ) : (
                      <Badge className={partnerComplete
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : transferred
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-slate-50 text-slate-600 border-slate-200"}>
                        {partnerLegacyState}
                      </Badge>
                    )}
                    hint={!agreementOwnsPartnerStage && !transferred && !partnerComplete ? "Member must be transferred to the regulated partner first." : null}
                    note={agreementOwnsPartnerStage ? (
                      <span data-testid="partner-onboarding-agreement-note">
                        This member is on the partner agreement flow; partner status is
                        tracked in Partner Stage (FinBPM) below. The legacy transfer
                        fields shown here no longer decide their status.
                      </span>
                    ) : null}
                  >
                    <Button
                      size="sm"
                      className="gap-1.5"
                      disabled={agreementOwnsPartnerStage || partnerComplete || !transferred || busy === "partner"}
                      onClick={markPartnerComplete}
                      data-testid="action-mark-partner-complete"
                    >
                      {busy === "partner" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      {partnerComplete ? "Onboarding complete" : "Mark partner onboarding complete"}
                    </Button>
                  </ActionBlock>

                  {/* 6) Fund transfer — normally driven by Paytiko webhook;
                       manual override for missed-webhook edge cases. Keyed on
                       the funding provider in play, not on agreement presence
                       (CAPMARKETS-118 shaping, question 3): with Paytiko off,
                       a legacy member's blank field is "not recorded" too. */}
                  <ActionBlock
                    icon={Banknote}
                    title="Fund Transfer"
                    statusBadge={fundingProvider === "finbpm_agreement" ? (
                      <Badge className="bg-slate-50 text-slate-600 border-slate-200"
                        data-testid="fund-transfer-legacy-badge">
                        legacy · {fundTransferLabel}
                      </Badge>
                    ) : (
                      <Badge className={fundTransferComplete
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-50 text-slate-600 border-slate-200"}>
                        {fundTransferLabel}
                      </Badge>
                    )}
                    hint={!fundTransferComplete && paytikoOrUnknown ? "Manual override — use only when Paytiko webhook is delayed or missed." : null}
                    note={fundingProvider === "finbpm_agreement" ? (
                      <span data-testid="fund-transfer-agreement-note">
                        Funding for this member is recorded on the partner agreement:
                        confirm funds in Partner Stage (FinBPM) below. Marking it here
                        does not confirm funds on the agreement, and dashboard
                        activation reads the agreement.
                      </span>
                    ) : fundingProvider === "none" ? (
                      <span data-testid="fund-transfer-no-provider-note">
                        Paytiko is not configured, so nothing updates this field
                        automatically. This manual record is its only source.
                      </span>
                    ) : null}
                  >
                    <Button
                      size="sm"
                      className="gap-1.5"
                      disabled={fundingProvider === "finbpm_agreement" || fundTransferComplete || busy === "fund-transfer"}
                      onClick={markFundTransferComplete}
                      data-testid="action-mark-fund-transfer-complete"
                    >
                      {busy === "fund-transfer" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      {fundTransferComplete ? "Fund transfer complete" : "Mark fund transfer complete"}
                    </Button>
                  </ActionBlock>

                  {/* 7) Partner stage (FinBPM) — agreement status, Monaxa
                       mapping, funds confirmation, activation, pilot
                       exception (finbpm-flow-007/010) */}
                  <PartnerStagePanel userId={activeUser.id} />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

// `hint` is a warning (amber); `note` explains why a card does not apply and
// is deliberately not amber, so an inapplicable card does not read as a
// blocker (CAPMARKETS-118).
const ActionBlock = ({ icon: Icon, title, statusBadge, hint, note, children }) => (
  <section className="p-4 rounded-lg border border-slate-200" data-testid={`block-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2">
        <Icon size={15} className="text-slate-500" />
        <p className="text-sm font-semibold text-slate-900">{title}</p>
      </div>
      {statusBadge}
    </div>
    {hint && <p className="text-[11px] text-amber-600 mb-2">{hint}</p>}
    {note && <p className="text-[11px] text-slate-600 mb-2">{note}</p>}
    {children}
  </section>
);

const PartnerStagePanel = ({ userId }) => {
  const [data, setData] = useState(null);
  const [identifier, setIdentifier] = useState("");
  const [pilotReason, setPilotReason] = useState("");
  const [finbpmRef, setFinbpmRef] = useState("");
  // STARSEED-CAPMARKETS-100: attestation for confirming funds when the member
  // funded out of band and so has no upload to give us.
  const [fundsAttestation, setFundsAttestation] = useState("");
  const [busy, setBusy] = useState(null);
  const [verifyLink, setVerifyLink] = useState("");     // doc-access-002: admin-minted shareable verify link
  const [accessLog, setAccessLog] = useState(null);     // doc-access-005: document access events

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/finbpm/admin/${userId}/agreement`);
      setData(r.data);
    } catch {
      setData(null);
    }
  }, [userId]);
  useEffect(() => {
    // Per-investor form state must never leak to the next investor file.
    setIdentifier("");
    setPilotReason("");
    setFinbpmRef("");
    setVerifyLink("");      // doc-access-002: never leak a minted link to the next file
    setAccessLog(null);     // doc-access-005
    setData(null);
    load();
  }, [userId, load]);

  const downloadProof = async (fileId, filename) => {
    try {
      const r = await api.get(`/finbpm/funding/proof/${fileId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "receipt";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the proof file");
    }
  };

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      await fn();
      toast.success(okMsg);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || detail?.message || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  if (!data) return null;
  const a = data.agreement;

  return (
    <Card className="mt-6 bg-white border-slate-200" data-testid="partner-stage-panel">
      <CardHeader>
        <CardTitle className="text-slate-900">Partner Stage (FinBPM)</CardTitle>
        <CardDescription className="text-slate-600">
          {data.status ? <>Status: <span className="font-semibold">{data.status}</span></> : "No agreement on file yet."}
          {a?.pilot_label && <span className="block mt-1 text-amber-700 text-xs">{a.pilot_label}</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {a && (
          <div className="text-sm text-slate-700 space-y-1">
            <p>Elected amount: <span className="font-semibold">${Number(a.amount_elected).toLocaleString()}</span></p>
            {a.partner_delivery && (
              <p>Partner delivery: <span className={a.partner_delivery.status === "sent" ? "text-emerald-700" : "text-red-700"}>
                {a.partner_delivery.status}{a.partner_delivery.error ? ` (${a.partner_delivery.error})` : ""}</span>
                {a.partner_delivery.status !== "sent" && (
                  <Button size="sm" variant="outline" className="ml-2 border-slate-300"
                    disabled={busy === "resend"}
                    onClick={() => act("resend", () => api.post("/finbpm/admin/resend-partner-email", { user_id: userId }), "Partner email re-sent")}
                    data-testid="finbpm-resend-btn">
                    Re-send to partner
                  </Button>
                )}
              </p>
            )}
            {(a.proofs || []).map((p2, i) => (
              <p key={`pf-${i}`}>
                Proof: {p2.filename || p2.tx_hash || "—"}
                {p2.file_id && (
                  <button type="button" className="ml-2 text-blue-700 underline"
                    onClick={() => downloadProof(p2.file_id, p2.filename)}>
                    download
                  </button>
                )}
              </p>
            ))}

            {/* doc-access-002: management view/download of the executed document
                (the canonical original — NOT the member's watermarked copy)
                + a revocable shareable verification link for a counterparty.
                CAPMARKETS-241: "sealed" dropped from the description for the
                same reason as the button label — whether the stored bytes
                carry an e-seal is a measured fact, not a property of this
                surface. */}
            {a.signed_pdf_file_id && (
              <div className="mt-2 rounded-md border border-slate-200 bg-white p-2"
                data-testid="admin-documents">
                <p className="font-medium text-slate-900">Executed agreement</p>
                <p className="text-xs text-slate-500">
                  Verification ID: {a.verification_id || "—"}
                </p>
                {/* CAPMARKETS-241: the seal state of the STORED bytes, measured
                    at countersign. Shown only once countersigned, because that
                    is the only artifact an e-seal is applied to — an
                    investor-signed-but-not-executed PDF is unsealed by design.
                    `null` is its own outcome: agreements executed before 241
                    were never measured and must not be reported either way. */}
                {a.partner_signed_at && (
                  <p className="text-xs" data-testid="admin-agreement-seal-state">
                    {a.signed_pdf_sealed === true ? (
                      <span className="text-emerald-700">E-seal: present</span>
                    ) : a.signed_pdf_sealed === false ? (
                      <span className="text-red-700">
                        E-seal: MISSING — stored unsealed
                      </span>
                    ) : (
                      <span className="text-slate-500">
                        E-seal: not recorded (executed before seal measurement)
                      </span>
                    )}
                  </p>
                )}
                {a.signed_pdf_sha256 && (
                  <p className="break-all font-mono text-[10px] text-slate-400">
                    SHA-256: {a.signed_pdf_sha256}
                  </p>
                )}
                <div className="mt-1 flex flex-wrap gap-2">
                  <button type="button" className="text-blue-700 underline"
                    onClick={() => downloadProof(
                      a.signed_pdf_file_id,
                      `agreement-${a.pcpa_agreement_number || "executed"}.pdf`,
                    )}
                    data-testid="admin-download-agreement">
                    {/* CAPMARKETS-241: this said "sealed original" while the
                        bytes it hands over were never sealed. The label now
                        claims only what the download IS; the seal state is
                        reported above, from a measurement of the stored bytes. */}
                    download executed original
                  </button>
                  {a.partner_signed_at && (
                    <button type="button" className="text-blue-700 underline"
                      disabled={busy === "resend-executed"}
                      onClick={() => act(
                        "resend-executed",
                        () => api.post("/finbpm/admin/resend-executed-copy", { user_id: userId }),
                        "Executed copy re-sent",
                      )}
                      data-testid="admin-resend-executed">
                      re-send executed copy
                    </button>
                  )}
                  <button type="button" className="text-red-700 underline"
                    disabled={busy === "supersede"}
                    onClick={() => {
                      const reason = window.prompt(
                        "Void this agreement? The sealed record is kept (never deleted); " +
                        "it's removed from 'latest' so a corrected one can be executed. Reason:");
                      if (!reason || !reason.trim()) return;
                      act(
                        "supersede",
                        () => api.post("/finbpm/admin/supersede-agreement",
                          { user_id: userId, reason: reason.trim() }),
                        "Agreement superseded",
                      );
                    }}
                    data-testid="admin-supersede">
                    supersede / void
                  </button>
                  <button type="button" className="text-blue-700 underline"
                    disabled={busy === "verify-link"}
                    onClick={() => act(
                      "verify-link",
                      async () => {
                        const r = await api.post(
                          "/admin/finbpm/agreement/verify-link", { user_id: userId });
                        setVerifyLink(r.data.url);
                      },
                      "Shareable verification link created",
                    )}
                    data-testid="admin-create-verify-link">
                    create verification link
                  </button>
                  {verifyLink && (
                    <button type="button" className="text-red-700 underline"
                      disabled={busy === "verify-link-revoke"}
                      onClick={() => act(
                        "verify-link-revoke",
                        async () => {
                          await api.delete(
                            `/admin/finbpm/agreement/verify-link?user_id=${encodeURIComponent(userId)}`);
                          setVerifyLink("");
                        },
                        "Verification link revoked",
                      )}
                      data-testid="admin-revoke-verify-link">
                      revoke link
                    </button>
                  )}
                </div>
                {verifyLink && (
                  <p className="mt-1 break-all text-xs text-emerald-700"
                    data-testid="admin-verify-link-url">
                    Share this link: {verifyLink}
                  </p>
                )}
                <div className="mt-1">
                  <button type="button" className="text-blue-700 underline"
                    disabled={busy === "access-log"}
                    onClick={() => act(
                      "access-log",
                      async () => {
                        const r = await api.get(
                          `/finbpm/admin/agreement/access-log?user_id=${encodeURIComponent(userId)}`);
                        setAccessLog(r.data.events || []);
                      },
                      "Access log loaded",
                    )}
                    data-testid="admin-access-log">
                    view access log
                  </button>
                  {accessLog && (
                    <ul className="mt-1 max-h-40 overflow-auto text-[10px] text-slate-500"
                      data-testid="admin-access-log-list">
                      {accessLog.length === 0 && <li>No access recorded yet.</li>}
                      {accessLog.map((e, i) => (
                        <li key={`al-${i}`}>
                          {e.ts} — {e.actor_role} {e.action}{e.ip ? ` (${e.ip})` : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* forge-20260612-008: controls mirror the backend's prerequisite
            guards (countersign -> mapping; proof -> funds; mapping + funds
            -> activation) instead of letting clicks discover them as 409s. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-900">Monaxa account identifier</Label>
            <Input
              value={identifier || data.monaxa_user_id || ""}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="assigned by FinBPM"
              disabled={!a?.partner_signed_at}
              className="bg-white text-slate-900 border-slate-300"
              data-testid="finbpm-monaxa-identifier"
            />
            <Button size="sm"
              disabled={busy === "map" || !a?.partner_signed_at || !(identifier || "").trim()}
              onClick={() => act("map", () => api.post("/finbpm/admin/monaxa-mapping", { user_id: userId, monaxa_account_identifier: identifier }), "Monaxa mapping saved")}
              className="bg-slate-900 text-white hover:bg-slate-800" data-testid="finbpm-map-btn">
              Save mapping
            </Button>
            {!a?.partner_signed_at && (
              <p className="text-[11px] text-slate-500">Available after the partner countersigns.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-slate-900">Stage controls</Label>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-800"
                disabled={busy === "funds" || !!a?.funds_confirmed_at
                  || (!a?.proof_uploaded_at && !fundsAttestation.trim())}
                onClick={() => act("funds", () => api.post("/finbpm/admin/funds-confirmed", {
                  user_id: userId,
                  ...(a?.proof_uploaded_at ? {} : { partner_confirmation: fundsAttestation.trim() }),
                }), "Funds confirmed — member notified")}
                data-testid="finbpm-funds-btn">
                {a?.funds_confirmed_at ? "Funds confirmed" : "Confirm funds received"}
              </Button>
              <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-800"
                disabled={busy === "activate" || !!a?.dashboard_activated_at
                  || !(data.attached_partner_accounts || []).length || !a?.funds_confirmed_at}
                onClick={() => act("activate", () => api.post("/finbpm/admin/activate-dashboard", { user_id: userId }), "Dashboard activated — member notified")}
                data-testid="finbpm-activate-btn">
                {a?.dashboard_activated_at ? "Dashboard active" : "Activate dashboard"}
              </Button>
            </div>
            {/* STARSEED-CAPMARKETS-100: a member who funded out of band has no
                upload to give us, and cannot produce one without first being
                emailed instructions for money they already sent. Recording who
                confirmed receipt — and how — unlocks confirmation without it.
                Stored as `partner_attested`, never as member-submitted proof. */}
            {!a?.proof_uploaded_at && !a?.funds_confirmed_at && (
              <div className="space-y-1" data-testid="finbpm-funds-attestation">
                <p className="text-[11px] text-slate-500">
                  No proof of payment uploaded. If the partner confirmed receipt directly,
                  record who confirmed it and through what channel to confirm without it.
                </p>
                <Input
                  value={fundsAttestation}
                  onChange={(e) => setFundsAttestation(e.target.value)}
                  placeholder="e.g. Milen confirmed on a call 2026-07-28; verified with FinBPM and Monaxa"
                  className="bg-white text-slate-900 border-slate-300"
                  data-testid="finbpm-funds-attestation-input"
                />
              </div>
            )}
            {a?.funds_confirmation_source?.kind === "partner_attested" && (
              <p className="text-[11px] text-amber-700" data-testid="finbpm-funds-attested-note">
                Confirmed without member proof — {a.funds_confirmation_source.attestation}
                {a.funds_confirmation_source.by ? ` (recorded by ${a.funds_confirmation_source.by})` : ""}
              </p>
            )}
            {!a?.dashboard_activated_at && (!(data.attached_partner_accounts || []).length || !a?.funds_confirmed_at) && (
              <p className="text-[11px] text-slate-500">
                {/* SAVED mapping only — an unsaved input doesn't satisfy it. */}
                Activation requires{!(data.attached_partner_accounts || []).length ? " an attached partner account" : ""}
                {!(data.attached_partner_accounts || []).length && !a?.funds_confirmed_at ? " and" : ""}
                {!a?.funds_confirmed_at ? " confirmed funds" : ""}.
              </p>
            )}
          </div>
        </div>

        {/* funding-instructions-003: the manual send (Kevin's decision) —
            countersign unlocks it; election changes prompt a re-send. */}
        <div className="border-t border-slate-200 pt-3 space-y-2" data-testid="funding-instructions-block">
          <Label className="text-slate-900">Funding instructions (emailed to the member)</Label>
          {a?.funding_instructions_email?.at ? (
            <p className="text-xs text-emerald-700">
              Emailed {new Date(a.funding_instructions_email.at).toLocaleString()} ·
              method: {a.funding_instructions_email.method}
              {a.funding_instructions_email.asset ? ` (${a.funding_instructions_email.asset})` : ""}
            </p>
          ) : (
            <p className="text-xs text-slate-500">
              Not sent yet{!a?.partner_signed_at ? " — unlocks after the partner countersigns" : ""}.
            </p>
          )}
          {a?.election_changed_after_instructions && (
            <p className="text-xs text-amber-700 font-semibold" data-testid="election-changed-hint">
              The member changed their funding method after the last email — re-send the instructions.
            </p>
          )}
          <p className="text-xs text-slate-500">
            Member elected: {a?.funding_method ? `${a.funding_method}${a.funding_asset ? ` · ${a.funding_asset}` : ""}` : "no election (email covers both methods)"}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={finbpmRef}
              onChange={(e) => setFinbpmRef(e.target.value)}
              placeholder="FinBPM reference (optional)"
              className="h-8 max-w-xs bg-white text-slate-900 border-slate-300"
              data-testid="finbpm-reference-input"
            />
            <Button size="sm" variant="outline" className="border-blue-300 text-blue-800"
              disabled={busy === "instructions" || !a?.partner_signed_at}
              onClick={() => act("instructions", () => api.post("/finbpm/admin/send-funding-instructions", {
                user_id: userId, finbpm_reference: finbpmRef || null,
              }), "Funding instructions emailed to the member")}
              data-testid="finbpm-send-instructions-btn">
              {a?.funding_instructions_email?.at ? "Re-send funding instructions" : "Send funding instructions"}
            </Button>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-3 space-y-2">
          <Label className="text-slate-900">Pilot exception (management-approved below-minimum)</Label>
          {data.finbpm_min_override ? (
            <p className="text-xs text-amber-700">{data.finbpm_min_override.label} — granted by {data.finbpm_min_override.by}</p>
          ) : (
            <div className="flex gap-2">
              <Input value={pilotReason} onChange={(e) => setPilotReason(e.target.value)}
                placeholder="management reason (required, audited)"
                className="bg-white text-slate-900 border-slate-300 flex-1" data-testid="finbpm-pilot-reason" />
              <Button size="sm" variant="outline" className="border-amber-300 text-amber-800"
                disabled={busy === "pilot" || !pilotReason.trim()}
                onClick={() => act("pilot", () => api.post("/finbpm/admin/pilot-exception", { user_id: userId, reason: pilotReason }), "Pilot exception granted")}
                data-testid="finbpm-pilot-btn">
                Grant exception
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminInvestorFilePage;
